/**
 * What a density histogram draws, resolved once from its payload (plots plan M).
 *
 * Shape only: every figure is the payload's. The band is drawn only where
 * Python allowed it (`isThinBase` false and both quartiles stamped), the
 * overflow tail is the bin Python stamped `openTop`, and the x-axis title is
 * the study's chart label for the measure's `sourceId` when there is one.
 */

import {
  DensityFormatter,
  DensityHistogramData,
  DensityHistogramPlotLabels,
  DensityHistogramSeriesData,
} from './format'

export interface DensityViewBin {
  id: string
  min: number
  max: number
  share: number
  count: number | null
  isOverflow: boolean
  label: string | null
}

export interface DensityViewSeries {
  id: string
  label: string | null
  base: number
  isThinBase: boolean
  bins: Array<DensityViewBin>
  median: number | null
  mean: number | null
  band: { low: number; high: number } | null
}

export interface DensityView {
  series: Array<DensityViewSeries>
  xAxisTitle: string | null
  /**
   * The x-axis title's source text when the chart prints another title
   * (`measure.fullLabel`, or the study's chart label): shown behind the (i).
   */
  xAxisFullTitle: string | null
  yAxisTitle: string | null
  medianWord: string | null
  meanWord: string | null
  bandWord: string | null
  formatter: DensityFormatter
}

function finite(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function viewSeries(series: DensityHistogramSeriesData, formatter: DensityFormatter): DensityViewSeries {
  const p25 = finite(series.stats.p25)
  const p75 = finite(series.stats.p75)
  const hasBand = !series.isThinBase && p25 != null && p75 != null

  return {
    id: series.id,
    label: series.label ?? null,
    base: series.base,
    isThinBase: series.isThinBase,
    bins: series.bins.map((bin, index) => ({
      id: bin.id,
      min: bin.min,
      max: bin.max,
      share: bin.share,
      count: bin.count ?? null,
      isOverflow: bin.labelForm === 'openTop',
      label: formatter.binLabel(bin, index),
    })),
    median: finite(series.stats.median),
    mean: finite(series.stats.mean),
    band: hasBand ? { low: Math.min(p25 as number, p75 as number), high: Math.max(p25 as number, p75 as number) } : null,
  }
}

/**
 * `chartLabels` is the study's map (`CanonicalAnalysisOutput.chartLabels`);
 * absent, the stamped `measure.label` is drawn, then the fallback template.
 */
export function densityView(
  data: Omit<DensityHistogramData, 'labels'> & { labels: DensityHistogramPlotLabels },
  formatter: DensityFormatter,
  chartLabels?: Record<string, string> | null,
): DensityView {
  const { measure, labels } = data
  const sourceLabel = measure.sourceId ? chartLabels?.[measure.sourceId] : undefined

  return {
    series: data.series.map((series) => viewSeries(series, formatter)),
    xAxisTitle: sourceLabel || measure.label || labels.continuous_density_x_axis_title_fallback || null,
    xAxisFullTitle: measure.fullLabel || (sourceLabel && measure.label && sourceLabel !== measure.label ? measure.label : null),
    yAxisTitle: labels.continuous_density_y_axis_title ?? null,
    medianWord: labels.median ?? null,
    meanWord: labels.mean ?? null,
    bandWord: labels.continuous_density_iqr_band_legend ?? null,
    formatter,
  }
}

/** D5/P1-3: candidate steps for the share axis ceiling, within a decade. */
const NICE_STEPS = [1, 2, 2.5, 5, 10]
/** D5: a near-uniform distribution never reads as a 0-10% axis. */
const MIN_Y_CEILING = 10
const MIN_TICK_INTERVALS = 4
const MAX_TICK_INTERVALS = 6
const TARGET_TICK_INTERVALS_CENTER = 5
const STEP_SEARCH_DECADES = [0.1, 1, 10]

function candidateSteps(effectiveMax: number): Array<number> {
  const magnitude = Math.pow(10, Math.floor(Math.log10(effectiveMax)))
  const steps = new Set<number>()
  for (const decade of STEP_SEARCH_DECADES) {
    for (const step of NICE_STEPS) {
      steps.add(step * magnitude * decade)
    }
  }
  return Array.from(steps).sort((a, b) => a - b)
}

/**
 * The share axis: the smallest round-stepped ceiling that clears the tallest
 * bar with 4 to 6 intervals (moved unchanged from `ContinuousDensitySlide`).
 * Presentation only: it picks gridlines, never a figure.
 */
export function densityShareAxis(maxValue: number): { ceiling: number; step: number } {
  const effectiveMax = Math.max(Number.isFinite(maxValue) ? maxValue : 0, MIN_Y_CEILING)
  let inWindow: { ceiling: number; step: number } | null = null
  let closest: { ceiling: number; step: number; distance: number } | null = null

  for (const step of candidateSteps(effectiveMax)) {
    if (step <= 0) {
      continue
    }
    const intervals = Math.max(1, Math.ceil(effectiveMax / step - 1e-9))
    const ceiling = step * intervals
    if (intervals >= MIN_TICK_INTERVALS && intervals <= MAX_TICK_INTERVALS) {
      if (!inWindow || ceiling < inWindow.ceiling) {
        inWindow = { ceiling, step }
      }
    }
    const distance = Math.abs(intervals - TARGET_TICK_INTERVALS_CENTER)
    if (!closest || distance < closest.distance || (distance === closest.distance && ceiling < closest.ceiling)) {
      closest = { ceiling, step, distance }
    }
  }

  return inWindow || closest || { ceiling: effectiveMax, step: effectiveMax }
}

/**
 * Value -> x on the regular (non-overflow) bins, where the median and the band
 * fall by construction: each bin owns an equal slot, so a value on an edge
 * lines up with its bar's edge.
 */
export function densityValueToX(value: number, regular: Array<DensityViewBin>, slotWidth: number, gap: number): number {
  if (!regular.length) {
    return 0
  }
  const first = regular[0]
  const last = regular[regular.length - 1]
  const clamped = Math.min(Math.max(value, first.min), last.max)
  let binIndex = regular.findIndex((bin) => clamped >= bin.min && clamped < bin.max)
  if (binIndex === -1) {
    binIndex = regular.length - 1
  }
  const bin = regular[binIndex]
  const span = bin.max - bin.min || 1
  const fraction = (clamped - bin.min) / span
  return binIndex * slotWidth + gap / 2 + fraction * Math.max(0, slotWidth - gap)
}
